import { useMemo, useState } from "react";

import { useProjectsList } from "../api";
import { projectDisplayName, projectSearchName } from "../utils/projectName";
import { useClickOutside } from "../utils/useClickOutside";
import styles from "./ProjectPicker.module.css";

interface ProjectPickerProps {
  projectIds: string[];
  onChange: (projectIds: string[]) => void;
  disabled?: boolean;
  // Toggle-button label shown when nothing is selected.
  placeholder: string;
  // The projects to offer, in place of every project - e.g. those one run worked.
  options?: { id: string; label: string; search?: string }[];
  // Offer only projects the daily scheduled run works (learning agents enabled).
  scheduledOnly?: boolean;
}

// Searchable, alphabetised multi-select of projects, showing the selection in the
// control itself. Remove a selection by reopening and unticking it.
export const ProjectPicker = ({
  projectIds,
  onChange,
  disabled,
  placeholder,
  options,
  scheduledOnly = false,
}: ProjectPickerProps) => {
  const projectsQuery = useProjectsList();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const wrapRef = useClickOutside<HTMLDivElement>(open, () => setOpen(false));

  const projects = useMemo(() => {
    const rows =
      options ??
      (projectsQuery.data ?? [])
        .filter((p) => !scheduledOnly || p.learning_agents_enabled === true)
        .map((p) => ({
          id: p.project_id,
          label: projectDisplayName(p),
          search: projectSearchName(p),
        }));
    return [...rows].sort((a, b) => a.label.localeCompare(b.label));
  }, [options, projectsQuery.data, scheduledOnly]);

  const q = search.trim().toLowerCase();
  const filtered = projects.filter(
    (p) =>
      !q ||
      (p.search ?? p.label).toLowerCase().includes(q) ||
      p.id.toLowerCase().includes(q),
  );
  const labelFor = (id: string): string =>
    projects.find((r) => r.id === id)?.label ?? id;
  const toggle = (id: string) =>
    onChange(
      projectIds.includes(id)
        ? projectIds.filter((x) => x !== id)
        : [...projectIds, id],
    );

  return (
    <div className={styles.picker} ref={wrapRef}>
      <button
        type="button"
        className={styles.toggle}
        onClick={() => setOpen((o) => !o)}
        disabled={disabled}
        aria-expanded={open}
      >
        {projectIds.length === 0 ? placeholder : projectIds.map(labelFor).join(", ")}
      </button>
      {open && (
        <div className={styles.panel}>
          <input
            type="search"
            className={styles.search}
            placeholder="Search projects..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            autoFocus
          />
          <div className={styles.bulk}>
            {/* All of what the search shows, added to what is already chosen. */}
            <button
              type="button"
              className={styles.bulkButton}
              disabled={filtered.length === 0}
              onClick={() =>
                onChange([
                  ...projectIds,
                  ...filtered.map((p) => p.id).filter((id) => !projectIds.includes(id)),
                ])
              }
            >
              Select all
            </button>
            <button
              type="button"
              className={styles.bulkButton}
              disabled={projectIds.length === 0}
              onClick={() => onChange([])}
            >
              Select none
            </button>
          </div>
          <div className={styles.list}>
            <div className={styles.sectionLabel}>Projects</div>
            {filtered.length === 0 ? (
              <p className={styles.empty}>No matching projects.</p>
            ) : (
              filtered.map((p) => (
                <label key={p.id} className={styles.item}>
                  <input
                    type="checkbox"
                    checked={projectIds.includes(p.id)}
                    onChange={() => toggle(p.id)}
                  />
                  <span>{p.label}</span>
                </label>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
};
